<template>
  <div :class="$style.variants" data-product-variant-selector-v="3.1.0">
    <div ref="swiper" :class="['swiper', $style.swiper]">
      <div :class="'swiper-wrapper'">
        <div
          v-for="(variant, index) in variants"
          :key="variant?.sys?.id"
          :class="['swiper-slide']"
        >
          <ProductVariantSelectorItem
            v-if="variant"
            :variant="variant"
            :is-active="selectedVariant?.sys?.id === variant?.sys?.id"
            @click="handleSetActiveVariant(variant?.sys?.id, index)"
          />
        </div>
      </div>
    </div>
    <div :class="$style.swiperControls">
      <button
        class="swiper-prev"
        :class="[
          $style.swiperPrev,
          !computedHasPrevSlide && $style.swiperControlDisabled,
        ]"
        :aria-label="localizedStrings['previous'] || 'Previous'"
        @click="handleSetActiveSlide(currentSlideIndex - 1)"
      >
        <IconChevronLeft :class="$style.chevronLeft" />
      </button>
      <button
        class="swiper-next"
        :class="[
          $style.swiperNext,
          !computedHasNextSlide && $style.swiperControlDisabled,
        ]"
        :aria-label="localizedStrings['next'] || 'Next'"
        @click="handleSetActiveSlide(currentSlideIndex + 1)"
      >
        <IconChevronRight :class="$style.chevronRight" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper'

import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const { localizedStrings } = useLocalizedStrings()
const siteMeasurementStore = useSiteMeasurementStore()

const emit = defineEmits(['set-active-variant'])

const MOBILE_SLIDES_PER_VIEW = 2.25
const TABLET_SLIDES_PER_VIEW = 2.5
const DESKTOP_SLIDES_PER_VIEW = 4.25

const props = defineProps<{
  variants: (TypeProductWithoutUnresolvableLinksResponse | undefined)[]
  selectedVariant?: TypeProductWithoutUnresolvableLinksResponse
}>()

// Data
const swiperElRef = useTemplateRef<HTMLElement>('swiper')
const swiperInstance = ref<null | Swiper>(null)
const currentVariantIndex = ref(
  Math.max(
    props.variants?.findIndex(
      (variant) => variant?.sys?.id === props.selectedVariant?.sys?.id,
    ) ?? 0,
    0,
  ),
)
const currentSlideIndex = ref(currentVariantIndex.value)

// Computed
const computedHasPrevSlide = computed(() => {
  const viewportWidth = siteMeasurementStore?.vWidth
  const slidesPerView =
    viewportWidth < 768
      ? MOBILE_SLIDES_PER_VIEW
      : viewportWidth < 1200
        ? TABLET_SLIDES_PER_VIEW
        : DESKTOP_SLIDES_PER_VIEW
  return currentSlideIndex.value > 0 && slidesPerView < props.variants?.length
})
const computedHasNextSlide = computed(() => {
  const viewportWidth = siteMeasurementStore?.vWidth
  const slidesPerView =
    viewportWidth < 768
      ? MOBILE_SLIDES_PER_VIEW
      : viewportWidth < 1200
        ? TABLET_SLIDES_PER_VIEW
        : DESKTOP_SLIDES_PER_VIEW
  return currentSlideIndex.value + slidesPerView < props.variants?.length
})

// Methods
const handleSetActiveSlide = (index: number) => {
  swiperInstance?.value?.slideTo?.(index)
  currentSlideIndex.value = index
}

const handleSetActiveVariant = (id: string, index: number) => {
  emit('set-active-variant', id)
  currentVariantIndex.value = index
  handleSetActiveSlide(index)
}

const initilizeSwiper = () => {
  if (swiperElRef.value) {
    swiperInstance.value = new Swiper(swiperElRef.value, {
      autoHeight: true,
      slidesPerView: MOBILE_SLIDES_PER_VIEW,
      slidesPerGroup: 1,
      spaceBetween: 10,
      watchSlidesProgress: true,
      allowTouchMove: false,
      navigation: {
        nextEl: '.swiper-next',
        prevEl: '.swiper-prev',
      },
      breakpoints: {
        768: {
          slidesPerView: TABLET_SLIDES_PER_VIEW,
        },
        1200: {
          slidesPerView: DESKTOP_SLIDES_PER_VIEW,
          spaceBetween: 10,
        },
      },
    })
    swiperInstance.value.slideTo(currentVariantIndex.value || 0, 0)
  }
}

onMounted(() => {
  initilizeSwiper()
})
</script>

<style lang="scss" module>
.variants {
  overflow: hidden;
  margin-left: -20px;
  padding: 20px;
  position: relative;

  @include media('sm') {
    max-width: clamp(25.813rem, 63.194vw + -4.521rem, 42.875rem);
  }

  @include media('lg') {
    max-width: 715px;
  }
}

.swiper {
  overflow: visible;
}

.swiperControls {
  align-content: flex-start;
  display: flex;
  height: 39px;
  justify-content: space-between;
  padding: 0 5px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-19px, calc(-50% - 5px));
  width: 100%;
  z-index: 1;

  @include media('lg') {
    padding: 0;
    width: calc(100% + 39px);
  }
}

.swiperPrev,
.swiperNext {
  align-items: center;
  background-color: var(--purple);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 39px;
  justify-content: center;
  pointer-events: auto;
  width: 39px;
}

.swiperControlDisabled {
  opacity: 0;
  pointer-events: none;
}

.chevronLeft,
.chevronRight {
  color: var(--white);
  height: 13px;
  width: 8px;
}

:global {
  :local(.variants) {
    .swiper-slide:not(.swiper-slide-visible) {
      opacity: 0.5;
    }
  }
}
</style>
